import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Typography, Button, Grid, Card, CardContent,
  TextField, InputAdornment, Chip, Paper
} from '@mui/material';
import { Search, TrendingUp, Security, LocalShipping, Support, SmartToy, LockOutlined } from '@mui/icons-material';

const categories = [
  { name: 'Industrial Machinery', icon: '🏭', count: 1250 },
  { name: 'Electronics', icon: '📱', count: 3400 },
  { name: 'Construction Materials', icon: '🏗️', count: 890 },
  { name: 'Automotive Parts', icon: '🚗', count: 2100 },
  { name: 'Energy & Solar', icon: '☀️', count: 760 },
  { name: 'Medical Equipment', icon: '🏥', count: 540 },
  { name: 'Agricultural Equipment', icon: '🌾', count: 430 },
  { name: 'Textiles & Fabrics', icon: '🧵', count: 1800 },
];

const features = [
  { icon: <Security sx={{ fontSize: 40 }} />, title: 'Secure Trading', desc: 'Escrow protection and verified suppliers ensure safe transactions' },
  { icon: <SmartToy sx={{ fontSize: 40 }} />, title: 'AI-Powered Search', desc: 'Smart product recommendations powered by artificial intelligence' },
  { icon: <LocalShipping sx={{ fontSize: 40 }} />, title: 'Global Shipping', desc: 'End-to-end logistics, customs clearance, and installation services' },
  { icon: <Support sx={{ fontSize: 40 }} />, title: 'Full Support', desc: 'Technical support, after-sales service, and dedicated account managers' },
];

const formatCategoryCount = (count) => `${count.toLocaleString()}+ products`;

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
        color: 'white', py: { xs: 4, md: 6 }, textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2rem', md: '3.5rem' } }}>
            {t('app_name')}
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, opacity: 0.9, fontSize: { xs: '1rem', md: '1.5rem' } }}>
            Your Trusted Global B2B Trade Platform
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.8, maxWidth: 600, mx: 'auto' }}>
            Connect with verified manufacturers, get competitive quotes, and manage your entire supply chain in one place.
          </Typography>

          {/* Search Box */}
          <Paper component="form" onSubmit={handleSearch}
            sx={{ display: 'flex', maxWidth: 600, mx: 'auto', borderRadius: 3, overflow: 'hidden' }}>
            <TextField
              fullWidth variant="outlined" placeholder="What are you looking for? Try: CNC machine, solar panels, forklift..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                sx: { '& fieldset': { border: 'none' } }
              }}
            />
            <Button type="submit" variant="contained" color="secondary"
              sx={{ borderRadius: 0, px: 4, fontSize: '1rem' }}>
              {t('search')}
            </Button>
          </Paper>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {['CNC Machines', 'Solar Panels', 'Forklifts', 'Electronics', 'Medical Equipment'].map(tag => (
              <Chip key={tag} label={tag} clickable onClick={() => navigate(`/products?search=${tag}`)}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* AI Sourcing Assistant Section — full expert use is for registered/logged-in users only */}
      <Box sx={{ bgcolor: '#f0f2f8', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 }, borderRadius: 4, textAlign: 'center',
              background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)', color: 'white'
            }}
          >
            <SmartToy sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="h4" fontWeight={800} gutterBottom>AI Sourcing Assistant</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 560, mx: 'auto' }}>
              Talk to Sam, our expert AI sourcing engineer — describe what you need or attach a photo, and get
              real product recommendations from our catalog with a clear explanation of why each one fits.
            </Typography>
            {isAuthenticated ? (
              <Button
                variant="contained" color="secondary" size="large" component={Link} to="/ai-assistant"
                sx={{ px: 5, py: 1.25, fontSize: '1rem' }}
              >
                Chat with Sam
              </Button>
            ) : (
              <>
                <Button
                  variant="contained" color="secondary" size="large" component={Link} to="/register"
                  startIcon={<LockOutlined />}
                  sx={{ px: 5, py: 1.25, fontSize: '1rem' }}
                >
                  Sign Up Free to Start
                </Button>
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.75 }}>
                  Free to use — registration required so our sourcing engineer can follow up on your requests.
                </Typography>
              </>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Typography variant="h4" textAlign="center" fontWeight={700} gutterBottom>
          Why Choose OSESG?
        </Typography>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
          A managed trade ecosystem that protects your business and simplifies international procurement
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: '0.3s' } }}>
                <Box sx={{ color: 'primary.main', mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>{feature.title}</Typography>
                <Typography variant="body2" color="text.secondary">{feature.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Categories Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" textAlign="center" fontWeight={700} gutterBottom>
            Browse Categories
          </Typography>
          <Grid container spacing={2} sx={{ mt: 3 }}>
            {categories.map((cat, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Card
                  component={Link} to={`/products?category=${cat.name}`}
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    minHeight: 220,
                    height: '100%',
                    width: '100%',
                    display: 'block',
                    boxSizing: 'border-box',
                    '&:hover': { transform: 'translateY(-4px)', transition: '0.3s', boxShadow: 4 }
                  }}
                >
                  <Box
                    sx={{
                      minHeight: 160,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      width: '100%'
                    }}
                  >
                    <Typography variant="h3" sx={{ mb: 1 }}>{cat.icon}</Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        minHeight: 56,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {cat.name}
                    </Typography>
                    <Chip
                      label={formatCategoryCount(cat.count)}
                      size="small"
                      sx={{
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#1a237e', color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Ready to Source Products Globally?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
            Join thousands of businesses that trust OSESG for their B2B sourcing needs.
          </Typography>
          <Button variant="contained" color="secondary" size="large" component={Link} to="/register"
            sx={{ px: 6, py: 1.5, fontSize: '1.1rem' }}>
            Get Started Free
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
